import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  Instagram, Youtube, Facebook, Linkedin, Twitter, Music2,
  Plus, Trash2, ExternalLink, ShieldAlert, CheckCircle2,
} from "lucide-react";

const PLATFORM_META = {
  instagram: { name: "Instagram", Icon: Instagram, color: "#E1306C" },
  tiktok: { name: "TikTok", Icon: Music2, color: "#000000" },
  twitter: { name: "X / Twitter", Icon: Twitter, color: "#1DA1F2" },
  linkedin: { name: "LinkedIn", Icon: Linkedin, color: "#0A66C2" },
  youtube: { name: "YouTube", Icon: Youtube, color: "#FF0000" },
  facebook: { name: "Facebook", Icon: Facebook, color: "#1877F2" },
};

// ---------- TikTok primary card ----------
function TikTokConnectCard() {
  const { lang } = useI18n();
  const loc = useLocation();
  const [status, setStatus] = useState(null); // {configured, connected, account, message_ar, message_en}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tiktok/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, connected: false, message_ar: "تعذّر التحقق من حالة TikTok.", message_en: "Could not load TikTok status." }));

  useEffect(() => {
    load();
    // Handle callback query params (?tiktok=connected|error&msg=...)
    const params = new URLSearchParams(loc.search);
    const t = params.get("tiktok");
    if (t === "connected") toast.success(lang === "ar" ? "تم ربط TikTok" : "TikTok connected");
    if (t === "error") {
      // msg is already Arabic when it comes from our callback
      const msg = decodeURIComponent(params.get("msg") || "");
      toast.error(msg || (lang === "ar" ? "فشل الربط" : "Connection failed"));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/tiktok/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = d?.[lang === "ar" ? "message_ar" : "message_en"] || (lang === "ar" ? "فشل بدء الربط" : "Failed to start connection");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/tiktok/disconnect");
      toast.success(lang === "ar" ? "تم فصل TikTok" : "TikTok disconnected");
      load();
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      // Try the primary /tiktok/sync route first. If Cloudflare/WAF returns
      // a 502 origin_bad_gateway (which has NO JSON detail body), we fall
      // back to the neutrally-named alias route /social/refresh-tt that
      // won't match WAF rules keyed on the "tiktok" URL segment.
      let data;
      try {
        const r = await api.post("/tiktok/sync");
        data = r.data;
      } catch (primaryErr) {
        const status = primaryErr?.response?.status;
        const hasJsonDetail = !!primaryErr?.response?.data?.detail;
        if (status === 502 && !hasJsonDetail) {
          // Almost certainly a Cloudflare/WAF-injected 502. Try alias.
          // eslint-disable-next-line no-console
          console.warn("tiktok/sync got upstream 502 without JSON detail — retrying via /social/refresh-tt");
          const r2 = await api.post("/social/refresh-tt");
          data = r2.data;
        } else {
          throw primaryErr;
        }
      }
      const followers = data?.account?.followers ?? 0;
      toast.success(lang === "ar"
        ? `تم تحديث الإحصائيات — ${followers.toLocaleString("ar-SA")} متابع`
        : `Stats refreshed — ${followers.toLocaleString()} followers`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.tiktok_error_code;
      const msg = d?.tiktok_error_message || d?.message_ar || d?.error || "";
      const status = e?.response?.status;
      const upstream = status === 502 && !d
        ? (lang === "ar" ? "خطأ من الشبكة الوسيطة (Cloudflare) — يرجى المحاولة بعد دقيقة"
                         : "Upstream network error (Cloudflare) — please retry in a minute")
        : "";
      toast.error(lang === "ar"
        ? `تعذّرت المزامنة${code ? ` (${code})` : ""}${msg ? ` — ${msg}` : ""}${upstream ? ` — ${upstream}` : ""}`
        : `Sync failed${code ? ` (${code})` : ""}${msg ? ` — ${msg}` : ""}${upstream ? ` — ${upstream}` : ""}`);
      // eslint-disable-next-line no-console
      console.error("tiktok/sync error", { status, data: e?.response?.data });
    } finally { setBusy(false); }
  };

  if (!status) {
    return (
      <div className="nx-card p-6" data-testid="tiktok-card-loading">
        <div className="text-sm text-muted-foreground">{lang === "ar" ? "جاري التحميل..." : "Loading…"}</div>
      </div>
    );
  }

  // Not configured — show clean Arabic message per spec
  if (!status.configured) {
    return (
      <div className="nx-card p-6" data-testid="tiktok-not-configured">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-sm bg-black text-white flex items-center justify-center shrink-0">
            <Music2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-display text-lg">TikTok</span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#B8875D]/10 text-[#B8875D] border border-[#B8875D]/30 rounded-sm">
                {lang === "ar" ? "المنصة الأساسية" : "Primary"}
              </span>
            </div>
            <div className="flex items-start gap-2 p-3 border border-amber-500/30 bg-amber-500/5 rounded-sm">
              <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm text-foreground" data-testid="tiktok-not-configured-msg">
                {lang === "ar"
                  ? "ربط TikTok غير مفعّل بعد. يرجى إضافة بيانات TikTok Developer."
                  : "TikTok connection is not enabled yet. Please add your TikTok Developer credentials."}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              {lang === "ar"
                ? "قم بإنشاء تطبيق على TikTok Developer Portal ثم اضف المفاتيح إلى متغيرات البيئة في الخادم."
                : "Create an app on the TikTok Developer Portal, then add the credentials to the server env."}
            </p>
            <a
              href="https://developers.tiktok.com/apps"
              target="_blank"
              rel="noreferrer"
              data-testid="tiktok-developer-link"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              {lang === "ar" ? "TikTok Developer Portal" : "TikTok Developer Portal"}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Configured & connected — show account
  if (status.connected && status.account) {
    const a = status.account;
    return (
      <div className="nx-card p-6" data-testid="tiktok-connected">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-sm bg-black text-white flex items-center justify-center shrink-0 overflow-hidden">
            {a.avatar_url ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" /> : <Music2 className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-display text-lg">TikTok</span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 rounded-sm">
                <CheckCircle2 className="w-2.5 h-2.5" /> {lang === "ar" ? "متصل" : "Connected"}
              </span>
            </div>
            <div className="text-sm font-medium truncate">{a.display_name || a.username || "—"}</div>
            {a.username && <div className="text-xs text-muted-foreground">@{a.username}</div>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              {[
                ["followers", lang === "ar" ? "المتابعون" : "Followers"],
                ["videos", lang === "ar" ? "الفيديوهات" : "Videos"],
                ["engagement", lang === "ar" ? "التفاعل" : "Engagement"],
                ["reach", lang === "ar" ? "الوصول" : "Reach"],
              ].map(([k, l]) => (
                <div key={k} className="p-3 border border-border/60 rounded-sm" data-testid={`tiktok-metric-${k}`}>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="font-display text-lg mt-1 tabular-nums">
                    {a[k] === null || a[k] === undefined ? "—" : Number(a[k]).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                data-testid="tiktok-sync-btn"
                onClick={syncNow}
                disabled={busy}
                className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 border border-primary/40 rounded-sm px-3 py-1.5 disabled:opacity-50"
              >
                {busy ? (lang === "ar" ? "جاري المزامنة..." : "Syncing...") : (lang === "ar" ? "مزامنة الآن" : "Sync now")}
              </button>
              <button
                data-testid="tiktok-disconnect-btn"
                onClick={disconnect}
                disabled={busy}
                className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" /> {lang === "ar" ? "فصل الحساب" : "Disconnect"}
              </button>
              <span className="text-[10px] text-muted-foreground">
                {lang === "ar" ? "الإحصائيات الحقيقية تظهر بعد أول مزامنة." : "Real metrics appear after the first sync."}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Configured but not yet connected — show connect CTA
  return (
    <div className="nx-card p-6" data-testid="tiktok-connect-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-sm bg-black text-white flex items-center justify-center shrink-0">
          <Music2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-display text-lg">TikTok</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#B8875D]/10 text-[#B8875D] border border-[#B8875D]/30 rounded-sm">
              {lang === "ar" ? "المنصة الأساسية" : "Primary"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {lang === "ar"
              ? "اربط حسابك على TikTok عبر تسجيل الدخول الرسمي (Login Kit) لجلب بياناتك الحقيقية."
              : "Connect your TikTok account via the official Login Kit to fetch your real metrics."}
          </p>
          <button
            data-testid="tiktok-connect-btn"
            onClick={connect}
            disabled={busy}
            className="mt-4 nx-btn-accent inline-flex items-center gap-2"
          >
            <Music2 className="w-4 h-4" />
            {busy
              ? (lang === "ar" ? "جاري التوجيه..." : "Redirecting…")
              : (lang === "ar" ? "ربط حساب TikTok" : "Connect TikTok account")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function SocialAccountsPage() {
  const { lang } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ platform: "instagram", handle: "", followers: 0 });

  const load = async () => setAccounts((await api.get("/social-accounts")).data.accounts);
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.handle) { toast.error("Enter handle"); return; }
    try {
      await api.post("/social-accounts", { ...form, followers: Number(form.followers) || 0, connected: true });
      setForm({ platform: "instagram", handle: "", followers: 0 });
      toast.success(lang === "ar" ? "تم الربط" : "Connected");
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => { await api.delete(`/social-accounts/${id}`); load(); };

  const otherAccounts = accounts.filter((a) => a.platform !== "tiktok");
  const otherPlatformOptions = Object.entries(PLATFORM_META).filter(([k]) => k !== "tiktok");

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "الحسابات الاجتماعية" : "Social Accounts"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {lang === "ar" ? "اربط قنواتك لبدء التتبع والنشر." : "Connect channels to start tracking and publishing."}
        </p>
      </div>

      {/* --- TikTok: primary integration (real OAuth) --- */}
      <div className="mb-6" data-testid="tiktok-section">
        <TikTokConnectCard />
      </div>

      {/* --- Other platforms: manual tracking until real OAuth ships --- */}
      <div className="mb-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          {lang === "ar" ? "منصات إضافية" : "Other platforms"}
        </div>
        <div className="text-[11px] text-muted-foreground/80 mt-1">
          {lang === "ar" ? "الربط الفعلي عبر OAuth للمنصات الأخرى سيصل قريبًا." : "Real OAuth for other platforms is coming soon."}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {otherAccounts.length === 0 && (
            <div className="col-span-full nx-card p-8 text-center text-muted-foreground" data-testid="social-empty">
              {lang === "ar" ? "لا توجد حسابات مرتبطة بعد." : "No accounts connected yet."}
            </div>
          )}
          {otherAccounts.map((a) => {
            const meta = PLATFORM_META[a.platform] || PLATFORM_META.instagram;
            const Icon = meta.Icon;
            return (
              <div key={a.id} data-testid={`social-account-${a.id}`} className="nx-card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-sm flex items-center justify-center text-white" style={{ background: meta.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{meta.name}</div>
                  <div className="text-xs text-muted-foreground truncate">@{a.handle}</div>
                  <div className="text-xs mt-1">{a.followers.toLocaleString()} {lang === "ar" ? "متابع" : "followers"}</div>
                </div>
                <button data-testid={`social-remove-${a.id}`} onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="nx-card p-5" data-testid="social-add-form">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            {lang === "ar" ? "ربط قناة جديدة" : "Connect new channel"}
          </div>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "المنصة" : "Platform"}</span>
            <select data-testid="social-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm">
              {otherPlatformOptions.map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
            </select>
          </label>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "المعرّف (بدون @)" : "Handle (without @)"}</span>
            <input data-testid="social-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.replace(/^@/, "") })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <label className="block text-xs mb-4">
            <span className="text-muted-foreground">{lang === "ar" ? "المتابعون الحاليون" : "Current followers"}</span>
            <input type="number" min="0" data-testid="social-followers" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <button data-testid="social-connect-btn" onClick={connect} className="nx-btn-primary w-full inline-flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> {lang === "ar" ? "ربط الحساب" : "Connect account"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
